'use client';

import { intervalToDuration } from 'date-fns';
import { useEffect, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { isFree } from '../../../utils/price';
import { c } from '../../../utils/tailwind-merge';
import { formatCountdown } from '../../../utils/time';
import { Badge } from '../../atoms/Badge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import type { ButtonVariant } from '../../atoms/Button/Button.v2.types';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Price } from '../../atoms/Price/Price';
import { QuantitySelector } from '../../molecules/QuantitySelector';
import { RichText } from '../../molecules/RichText/RichText';
import {
  ProductPricingProps,
  ProductPricingPropsActions,
} from './ProductPricing.types';

type ProductPricingPrimaryCta =
  | 'none'
  | 'add-to-assets'
  | 'buy-now'
  | 'add-to-cart'
  | 'in-cart'
  | 'request-seat'
  | 'open-in-unity'
  | 'manage-seats';

type ProductPricingHelpText =
  | 'none'
  | 'eula'
  | 'refund-policy'
  | 'org-owns-unassigned-seats'
  | 'org-owns-unassigned-seats-count'
  | 'org-has-no-unassigned-seats';

type ProductPricingNotice =
  'none' | 'another-org-owns' | 'already-own-another-org' | 'region-restricted';

type ProductPricingSecondaryHelpText = Extract<
  ProductPricingHelpText,
  'none' | 'org-has-no-unassigned-seats'
>;

type ProductPricingView = {
  showQuantitySelector: boolean;
  notice: ProductPricingNotice;
  primaryCta: ProductPricingPrimaryCta;
  helpText: ProductPricingHelpText;
  showSecondaryCta: boolean;
  secondaryHelpText: ProductPricingSecondaryHelpText;
};

// Single source of truth for the pricing layout. Maps the (pricing,
// entitlement, userType, seat, org seat count) matrix to an explicit view model
// so the JSX stays declarative. Missing userType is treated as 'user' and
// missing isSeatAssigned as assigned (the safe Open-in-Unity default). A known
// seat count of 0 or less means the org has no unassigned seats; an unknown
// count suppresses all unassigned-seat help texts. Buy now is offered only
// when the active org does not own the asset; an owner of an owned asset adds
// seats through Add/manage seats instead.
const resolveProductPricingView = ({
  entitlement,
  price,
  userType,
  isSeatAssigned,
  unassignedSeatCount,
  hasSeatData,
}: Pick<
  ProductPricingProps,
  | 'entitlement'
  | 'price'
  | 'userType'
  | 'isSeatAssigned'
  | 'unassignedSeatCount'
  | 'hasSeatData'
>): ProductPricingView => {
  const isPaid = Boolean(price.originalPrice);
  const base: ProductPricingView = {
    showQuantitySelector: false,
    notice: 'none',
    primaryCta: 'open-in-unity',
    helpText: 'none',
    showSecondaryCta: false,
    secondaryHelpText: 'none',
  };

  // Claim and purchase both fail for this org, so explain instead of offering them.
  if (entitlement === 'region-restricted') {
    return { ...base, notice: 'region-restricted', primaryCta: 'none' };
  }

  if (!isPaid) {
    if (entitlement === 'not-entitled') {
      return { ...base, primaryCta: 'add-to-assets', helpText: 'eula' };
    }
    if (entitlement === 'entitled-another-org') {
      return {
        ...base,
        notice: 'another-org-owns',
        primaryCta: 'add-to-assets',
        helpText: 'eula',
      };
    }
    return base;
  }

  if (entitlement === 'preview') {
    return base;
  }

  if (entitlement === 'not-entitled') {
    return {
      ...base,
      showQuantitySelector: true,
      primaryCta: 'buy-now',
      helpText: 'refund-policy',
    };
  }

  const seatAssigned = isSeatAssigned ?? true;
  const hasSeats = hasSeatData ?? false;
  const seatCount = unassignedSeatCount ?? 0;
  const hasUnassignedSeats = hasSeats && seatCount > 0;
  const hasNoUnassignedSeats = hasSeats && seatCount <= 0;
  const noSeatsLeftHelpText: ProductPricingSecondaryHelpText =
    hasNoUnassignedSeats ? 'org-has-no-unassigned-seats' : 'none';
  const type = userType ?? 'user';

  if (entitlement === 'entitled-current-org') {
    if (!seatAssigned) {
      if (type === 'admin') {
        return {
          ...base,
          primaryCta: 'manage-seats',
          helpText: hasUnassignedSeats
            ? 'org-owns-unassigned-seats-count'
            : noSeatsLeftHelpText,
        };
      }
      return {
        ...base,
        primaryCta: 'request-seat',
        helpText: hasUnassignedSeats ? 'org-owns-unassigned-seats' : 'none',
      };
    }
    if (type === 'admin') {
      return {
        ...base,
        showSecondaryCta: true,
        secondaryHelpText: noSeatsLeftHelpText,
      };
    }
    return base;
  }

  // entitled-another-org (userType-agnostic).
  return {
    ...base,
    showQuantitySelector: true,
    notice: seatAssigned ? 'already-own-another-org' : 'another-org-owns',
    primaryCta: 'buy-now',
    helpText: 'refund-policy',
  };
};

// With the multi-product cart on, the plain purchase CTA (a paid product nobody
// in the user's orgs owns) appends to the cart instead of replacing it. Buy now
// for an asset another of the user's orgs owns keeps its CTA and layout. A
// product that is already a line drops the seat picker: appending again would
// silently bump that line's seats.
const withAddToCart = (
  view: ProductPricingView,
  {
    entitlement,
    isAddToCartEnabled,
    isInCart,
  }: Pick<
    ProductPricingProps,
    'entitlement' | 'isAddToCartEnabled' | 'isInCart'
  >
): ProductPricingView => {
  if (
    !isAddToCartEnabled ||
    entitlement !== 'not-entitled' ||
    view.primaryCta !== 'buy-now'
  ) {
    return view;
  }
  if (isInCart) {
    return { ...view, primaryCta: 'in-cart', showQuantitySelector: false };
  }
  return { ...view, primaryCta: 'add-to-cart' };
};

const primaryCtaLabel = (
  primaryCta: ProductPricingPrimaryCta,
  microcopy: ProductPricingProps['microcopy']
): string => {
  switch (primaryCta) {
    case 'none':
      return '';
    case 'add-to-assets':
      return microcopy.addToMyAssetSinglePackage;
    case 'buy-now':
      return microcopy.buyNow;
    case 'add-to-cart':
      return microcopy.addToCart;
    case 'in-cart':
      return microcopy.inCart;
    case 'request-seat':
      return microcopy.requestSeat;
    case 'open-in-unity':
      return microcopy.openInUnity;
    case 'manage-seats':
      return microcopy.manageSeats;
  }
};

const primaryCtaDataTest: Record<
  Exclude<ProductPricingPrimaryCta, 'none'>,
  string
> = {
  'add-to-assets': 'add-to-my-assets-button',
  'buy-now': 'buy-now-button',
  'add-to-cart': 'add-to-cart-button',
  'in-cart': 'in-cart-button',
  'request-seat': 'request-seat-button',
  'open-in-unity': 'open-in-unity-button',
  'manage-seats': 'product-info-manage-seats-button',
};

const primaryCtaIconRight: Partial<Record<ProductPricingPrimaryCta, string>> = {
  'request-seat': 'UserPlus',
  'manage-seats': 'UserPlus',
};

const primaryCtaVariant: Partial<
  Record<ProductPricingPrimaryCta, ButtonVariant>
> = {
  'manage-seats': 'secondary-blue-monochrome',
};

const renderCtaButton = ({
  label,
  primaryCta,
  isLoading,
  className,
  onClick,
}: {
  label: string;
  primaryCta: ProductPricingPrimaryCta;
  isLoading?: boolean;
  className?: string;
  onClick: () => void;
}) => {
  if (primaryCta === 'none') {
    return null;
  }
  const iconRight = primaryCtaIconRight[primaryCta];
  return (
    <ButtonV2
      label={label}
      variant={primaryCtaVariant[primaryCta] ?? 'primary'}
      size="large"
      shape="square"
      onClick={onClick}
      className={c(
        'min-w-0 flex-1',
        iconRight && !isLoading && 'gap-x-2',
        className
      )}
      disabled={isLoading}
      aria-busy={isLoading}
      iconLeft={isLoading ? 'SpinnerGap' : undefined}
      iconRight={isLoading ? undefined : iconRight}
      iconWeight="bold"
      iconClassName={isLoading ? 'animate-spin' : undefined}
      iconSize={20}
      data-test={primaryCtaDataTest[primaryCta]}
    />
  );
};

const renderSecondaryCtaButton = ({
  microcopy,
  onManageSeats,
}: Pick<ProductPricingProps, 'microcopy'> &
  Pick<ProductPricingPropsActions, 'onManageSeats'>) => {
  return (
    <ButtonV2
      label={microcopy.manageSeats}
      variant="secondary-blue-monochrome"
      size="large"
      shape="square"
      className="w-full gap-x-2"
      iconRight="UserPlus"
      iconWeight="bold"
      iconSize={20}
      onClick={() => onManageSeats?.()}
      data-test="product-info-manage-seats-button"
    />
  );
};

const renderFavoriteButton = ({
  isFavorite,
  microcopy,
  onFavorite,
}: Pick<ProductPricingProps, 'isFavorite' | 'microcopy' | 'onFavorite'>) => {
  if (!onFavorite) {
    return null;
  }

  const isLoading = isFavorite == null;

  return (
    <ButtonV2
      variant={isFavorite ? 'primary' : 'secondary-blue-monochrome'}
      size="large"
      shape="square"
      iconLeft={isLoading ? 'SpinnerGap' : 'BookmarkSimple'}
      iconWeight={isFavorite ? 'fill' : 'bold'}
      iconClassName={isLoading ? 'animate-spin' : undefined}
      iconSize={20}
      aria-label={
        isLoading
          ? microcopy.favoritesLoading
          : isFavorite
            ? microcopy.removeFromFavorites
            : microcopy.addToFavorites
      }
      aria-pressed={isLoading ? undefined : Boolean(isFavorite)}
      disabled={isLoading}
      className="shrink-0"
      data-test="product-info-favorite-button"
      onClick={() => onFavorite()}
    />
  );
};

const helpTextClassName = 'text-xs text-grey-700 [&_p]:mb-0';

const renderHelpText = ({
  helpText,
  customEula,
  unassignedSeatCount,
  microcopy,
}: {
  helpText: ProductPricingHelpText;
} & Pick<
  ProductPricingProps,
  'customEula' | 'unassignedSeatCount' | 'microcopy'
>) => {
  let html: string | undefined;
  switch (helpText) {
    case 'eula':
      html = customEula
        ? interpolateStringWithData(microcopy.customEulaMessage, {
            custom: customEula,
          })
        : microcopy.standardEulaMessage;
      break;
    case 'refund-policy':
      html = microcopy.refundPolicy;
      break;
    case 'org-owns-unassigned-seats':
      html = microcopy.orgOwnsUnassignedSeats;
      break;
    case 'org-owns-unassigned-seats-count':
      html = interpolateStringWithData(
        microcopy.orgOwnsUnassignedSeatsWithCount,
        { count: unassignedSeatCount ?? 0 }
      );
      break;
    case 'org-has-no-unassigned-seats':
      html = microcopy.orgHasNoUnassignedSeats;
      break;
    case 'none':
      return null;
  }

  if (!html) {
    return null;
  }

  return (
    <RichText
      html={html}
      className={helpTextClassName}
      linkComponent={({ href, children, target, rel }) => (
        <LinkV2
          href={href}
          openInANewTab={target === '_blank'}
          variant="link-in-text-primary"
          rel={rel}
        >
          {children}
        </LinkV2>
      )}
    />
  );
};

const renderNotice = (
  notice: ProductPricingNotice,
  microcopy: ProductPricingProps['microcopy']
) => {
  if (notice === 'none') {
    return null;
  }

  const text =
    notice === 'another-org-owns'
      ? microcopy.anotherOrgOwnsAsset
      : notice === 'region-restricted'
        ? microcopy.regionRestrictedOrganization
        : microcopy.alreadyOwnInAnotherOrg;

  return (
    <p
      className="text-xs leading-5 tracking-[-0.024px] text-grey-700"
      data-test={`product-pricing-notice-${notice}`}
    >
      {text}
    </p>
  );
};

export const ProductPricing = ({
  variant = 'default',
  entitlement,
  price,
  sale,
  customEula,
  userType,
  isFavorite,
  isPurchasing = false,
  isClaiming = false,
  isAddToCartEnabled,
  isInCart,
  quantity: selectedQuantity,
  onQuantityChange,
  isSeatAssigned,
  unassignedSeatCount,
  hasSeatData,
  microcopy,
  className = '',
  onAddToMyAssets,
  onBuyNow,
  onAddToCart,
  onRequestSeat,
  onOpenInUnity,
  onFavorite,
  onManageSeats,
}: ProductPricingProps) => {
  const [localQuantity, setLocalQuantity] = useState(1);
  const quantity = selectedQuantity ?? localQuantity;
  const setQuantity = onQuantityChange ?? setLocalQuantity;
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (variant !== 'default' || !sale?.dates?.ends) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [variant, sale?.dates?.ends]);

  const isPreviewMode = entitlement === 'preview';

  const view = withAddToCart(
    resolveProductPricingView({
      entitlement,
      price,
      userType,
      isSeatAssigned,
      unassignedSeatCount,
      hasSeatData,
    }),
    { entitlement, isAddToCartEnabled, isInCart }
  );

  const ctaLabel = primaryCtaLabel(view.primaryCta, microcopy);
  const isCtaLoading = isPurchasing || isClaiming;

  const handlePrimaryCta = () => {
    switch (view.primaryCta) {
      case 'none':
        return;
      case 'add-to-assets':
        return onAddToMyAssets?.();
      case 'buy-now':
        return onBuyNow?.(quantity);
      case 'add-to-cart':
      case 'in-cart':
        return onAddToCart?.(quantity);
      case 'request-seat':
        return onRequestSeat?.(quantity);
      case 'open-in-unity':
        return onOpenInUnity?.();
      case 'manage-seats':
        return onManageSeats?.();
    }
  };

  const saleCountdownFormatted =
    variant === 'default' && sale?.dates?.ends && sale.dates.ends > now
      ? formatCountdown(
          sale.dates.ends,
          intervalToDuration({ start: now, end: sale.dates.ends }),
          {
            withDays: microcopy.sale.countdownWithDays,
            hoursOnly: microcopy.sale.countdownHoursOnly,
          }
        )
      : null;

  return variant === 'default' ? (
    <div className={c('flex flex-col gap-y-4', className)}>
      <div className="space-y-1">
        <div className="flex gap-2">
          <Price
            className={c(
              'text-grey-900',
              isFree(price)
                ? 'text-md font-normal leading-7 tracking-[-0.108px]'
                : 'text-xl font-medium leading-8 tracking-[-0.72px]'
            )}
            value={isFree(price) ? undefined : String(price.currentPrice)}
            currency={price.currency}
            microcopy={{ free: microcopy.free }}
          />
          {price.currentPrice !== price.originalPrice && (
            <Price
              className="text-xl font-medium leading-8 tracking-[-0.72px] text-grey-400 line-through"
              value={String(price.originalPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />
          )}
        </div>
        {(price.discountPercent || sale) && (
          <div className="flex gap-2">
            {sale?.type === 'new_release_discount' && (
              <Badge variant="default" text={microcopy.sale.newRelease} />
            )}
            {Boolean(price.discountPercent) && (
              <Badge variant="sale" text={`-${price.discountPercent}%`} />
            )}

            {saleCountdownFormatted && (
              <p
                suppressHydrationWarning
                className="text-xs leading-5 tracking-[-0.024px] text-grey-700"
              >
                {microcopy.sale.endsIn} {saleCountdownFormatted}
              </p>
            )}
          </div>
        )}
      </div>

      {view.showQuantitySelector ? (
        <div className="space-y-1">
          <div className="text-sm leading-5 tracking-[-0.028px] text-grey-700">
            {microcopy.seats}
          </div>
          <QuantitySelector
            value={quantity}
            onDecreaseQuantity={() => setQuantity(quantity - 1)}
            onIncreaseQuantity={() => setQuantity(quantity + 1)}
            microcopy={microcopy.quantitySelector}
          />
        </div>
      ) : (
        <></>
      )}

      {isPreviewMode ? (
        <div>{renderFavoriteButton({ isFavorite, microcopy, onFavorite })}</div>
      ) : (
        <div>
          <div className="space-y-2">
            {renderNotice(view.notice, microcopy)}
            <div className="flex gap-2">
              {renderCtaButton({
                label: ctaLabel,
                primaryCta: view.primaryCta,
                isLoading: isCtaLoading,
                onClick: handlePrimaryCta,
              })}
              {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
            </div>
            {renderHelpText({
              helpText: view.helpText,
              customEula,
              unassignedSeatCount,
              microcopy,
            })}
          </div>
          {view.showSecondaryCta && (
            <div
              className={c(
                'space-y-1',
                view.helpText === 'none' ? 'mt-2' : 'mt-6'
              )}
            >
              {renderSecondaryCtaButton({ microcopy, onManageSeats })}
              {renderHelpText({ helpText: view.secondaryHelpText, microcopy })}
            </div>
          )}
        </div>
      )}
    </div>
  ) : (
    <div
      className={c(
        'grid w-full grid-cols-[auto_auto] items-center justify-between gap-x-4 gap-y-2 lg:max-w-[496px] lg:grid-cols-[auto_1fr] lg:gap-x-8',
        className
      )}
    >
      <div className="space-y-1">
        <div className="flex gap-2">
          <Price
            className="text-base font-normal leading-[22px] text-grey-900 lg:text-md lg:leading-[26px]"
            value={isFree(price) ? undefined : String(price.currentPrice)}
            currency={price.currency}
            microcopy={{ free: microcopy.free }}
          />
          {price.currentPrice !== price.originalPrice && (
            <Price
              className="text-base font-normal leading-[22px] text-grey-400 line-through lg:text-md lg:leading-[26px]"
              value={String(price.originalPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />
          )}
        </div>
        {(price.discountPercent || sale) && (
          <div className="flex gap-2">
            {sale?.type === 'new_release_discount' && (
              <Badge variant="default" text={microcopy.sale.newRelease} />
            )}
            {Boolean(price.discountPercent) && (
              <Badge variant="sale" text={`-${price.discountPercent}%`} />
            )}
          </div>
        )}
      </div>

      {isPreviewMode ? (
        <div className="flex gap-2">
          {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
        </div>
      ) : (
        <div className="flex gap-2">
          {renderCtaButton({
            label: ctaLabel,
            primaryCta: view.primaryCta,
            isLoading: isCtaLoading,
            className:
              'whitespace-normal px-4 text-center lg:px-[22px] [&_svg]:shrink-0',
            onClick: handlePrimaryCta,
          })}
          {renderFavoriteButton({
            isFavorite,
            microcopy,
            onFavorite,
          })}
        </div>
      )}

      {!isPreviewMode &&
        (view.notice !== 'none' || view.helpText !== 'none') && (
          <div className="col-span-2 space-y-2 lg:col-span-1 lg:col-start-2">
            {renderNotice(view.notice, microcopy)}
            {renderHelpText({
              helpText: view.helpText,
              customEula,
              unassignedSeatCount,
              microcopy,
            })}
          </div>
        )}
    </div>
  );
};
